import { Link, useNavigate, ClientOnly } from "@tanstack/react-router";
import { ChefHat, LogOut, LayoutDashboard, User, Settings, Shield, Eye } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "./theme-toggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/use-auth";
import { LaunchCounter } from "./launch-counter";
import { NotificationsBell } from "./notifications-bell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function SiteHeader() {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Sign-out hygiene: stop pending fetches, drop cached protected data,
  // poi sign-out e replace verso /auth (la route protetta non deve restare in history).
  const handleSignOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    // Dopo logout torniamo alla homepage pubblica, non a /auth.
    navigate({ to: "/", replace: true });
  };
  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-gold transition-transform group-hover:scale-105">
            <ChefHat className="h-4 w-4" />
          </span>
          <span className="font-display text-xl tracking-tight">
            Chef<span className="text-gold">&nbsp;Connect</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <Link to="/search" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            Trova chef
          </Link>
          <Link to="/mappa" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            Mappa
          </Link>
          <Link to="/come-funziona" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            Come funziona
          </Link>
          <Link to="/premium" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            Premium
          </Link>
          {!isAdmin && (
            <Link to="/diventa-chef" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              Sei uno chef?
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {!isAdmin && <LaunchCounter className="hidden lg:inline-flex" />}
          {isAdmin && (
            <>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:inline-flex"
                title="Apri la homepage pubblica in una nuova scheda"
              >
                <Eye className="h-3.5 w-3.5" /> Visualizza il sito
              </a>
              <span
                className="hidden items-center gap-1 rounded-md border border-gold/40 bg-gold-muted/30 px-2 py-1 text-[11px] font-medium text-foreground sm:inline-flex"
                title="Modalità Amministratore"
              >
                <Shield className="h-3.5 w-3.5 text-gold" /> Admin
              </span>
            </>
          )}
          <ThemeToggle />
          {user && <NotificationsBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <User className="mr-1.5 h-4 w-4" />
                  {user.email?.split("@")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Dashboard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/account"><Settings className="mr-2 h-4 w-4" />Impostazioni</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <ClientOnly fallback={null}>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
                  <Link to="/auth" search={{ mode: "signin" }}>Accedi</Link>
                </Button>
                <Button asChild size="sm" className="bg-foreground text-background hover:bg-foreground/90">
                  <Link to="/auth" search={{ mode: "signup" }}>Registrati</Link>
                </Button>
              </div>
            </ClientOnly>
          )}
        </div>
      </div>
    </header>
  );
}